/**
 * DIPS2.0 登録機体一覧ページ - Retro-Brutalistic デザインシステム
 */

import React, { useState, useEffect, useCallback } from 'react';
import { dipsApi } from '../services/api';
import { Icon } from '../components/icons';

// ===== Retro-Brutalistic UI コンポーネント =====

function ErrorAlert({ message }) {
  if (!message) return null;
  return (
    <div
      className="flex items-start gap-2 px-4 py-3 rounded-lg border-2"
      style={{ backgroundColor: '#FEF2F2', borderColor: '#DC2626', color: '#991B1B', fontFamily: 'Inter, sans-serif' }}
    >
      <span className="material-symbols-outlined flex-shrink-0" style={{ fontSize: '18px', marginTop: '1px' }}>error</span>
      <span className="text-sm">{message}</span>
    </div>
  );
}

function SuccessAlert({ message }) {
  if (!message) return null;
  return (
    <div
      className="flex items-start gap-2 px-4 py-3 rounded-lg border-2"
      style={{ backgroundColor: '#F0FFF4', borderColor: '#216d1c', color: '#216d1c', fontFamily: 'Inter, sans-serif' }}
    >
      <span className="material-symbols-outlined flex-shrink-0" style={{ fontSize: '18px', marginTop: '1px', fontVariationSettings: "'FILL' 1" }}>check_circle</span>
      <span className="text-sm">{message}</span>
    </div>
  );
}

function Spinner({ size = 'md' }) {
  const dim = size === 'lg' ? 'w-12 h-12' : 'w-8 h-8';
  return (
    <svg className={`animate-spin ${dim} text-[#0068b7]`} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function QuestWindow({ children, className = '', padding = 'p-6' }) {
  return (
    <div
      className={`relative bg-white border-4 border-[#0068B7] rounded-xl ${padding} ${className}`}
      style={{ boxShadow: '0 8px 0 0 #00508e' }}
    >
      <div className="absolute inset-2 border-2 border-[#0068B7]/20 rounded-lg pointer-events-none" />
      <div className="relative z-10">{children}</div>
    </div>
  );
}

function AeroButton({ onClick, disabled, isLoading, children, variant = 'primary', type = 'button', size = 'md' }) {
  const sizeClass = size === 'sm' ? 'px-4 py-2 text-sm' : size === 'lg' ? 'px-10 py-4 text-lg' : 'px-6 py-2.5 text-sm';

  const variants = {
    primary: {
      className: `text-white bg-[#0068B7] hover:bg-[#0057a0] active:translate-y-0.5`,
      style: { boxShadow: '0 4px 0 0 #00508e' },
      disabledStyle: { boxShadow: 'none' },
    },
    secondary: {
      className: `text-[#0068B7] bg-white border-2 border-[#0068B7] hover:bg-blue-50 active:translate-y-0.5`,
      style: { boxShadow: '0 4px 0 0 #0068B740' },
      disabledStyle: { boxShadow: 'none' },
    },
    danger: {
      className: `text-white bg-[#DC2626] hover:bg-[#B91C1C] active:translate-y-0.5`,
      style: { boxShadow: '0 4px 0 0 #991B1B' },
      disabledStyle: { boxShadow: 'none' },
    },
    success: {
      className: `text-white bg-[#216d1c] hover:bg-[#1a5716] active:translate-y-0.5`,
      style: { boxShadow: '0 4px 0 0 #164a12' },
      disabledStyle: { boxShadow: 'none' },
    },
  };

  const v = variants[variant] || variants.primary;
  const isDisabled = disabled || isLoading;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      className={`inline-flex items-center justify-center gap-2 ${sizeClass} rounded-full font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${v.className}`}
      style={{
        fontFamily: 'Space Grotesk, sans-serif',
        fontWeight: 600,
        ...(isDisabled ? v.disabledStyle : v.style),
      }}
    >
      {isLoading ? <Spinner size="sm" /> : null}
      {children}
    </button>
  );
}

function StatusBadge({ status }) {
  const styles = {
    active:   { bg: '#F0FFF4', border: '#216d1c', text: '#216d1c', label: '有効' },
    expired:  { bg: '#FFFBEB', border: '#a44e01', text: '#7f3b00', label: '期限切れ' },
    deleted:  { bg: '#F3F4F6', border: '#717782', text: '#414751', label: '抹消済' },
    linked:   { bg: '#F0FFF4', border: '#216d1c', text: '#216d1c', label: '連携済み' },
    required: { bg: '#F3F4F6', border: '#c1c7d3', text: '#717782', label: '要認証' },
  };
  const s = styles[status] || styles.deleted;
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border-2"
      style={{ backgroundColor: s.bg, borderColor: s.border, color: s.text, fontFamily: 'Space Grotesk, sans-serif' }}
    >
      {s.label}
    </span>
  );
}

function SectionLabel({ children }) {
  return (
    <span
      style={{
        fontFamily: 'Space Grotesk, sans-serif',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: '#717782',
      }}
    >
      {children}
    </span>
  );
}

function SectionHeader({ icon, title, subtitle, actions }) {
  return (
    <div className="flex items-center justify-between pb-3 mb-5" style={{ borderBottom: '2px solid rgba(0,104,183,0.2)' }}>
      <div className="flex items-center gap-3">
        {icon && (
          <span
            className="material-symbols-outlined text-[#0068B7]"
            style={{ fontSize: '22px', fontVariationSettings: "'FILL' 1" }}
          >
            {icon}
          </span>
        )}
        <div>
          <h3 style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: '16px', color: '#0068B7' }}>
            {title}
          </h3>
          {subtitle && <SectionLabel>{subtitle}</SectionLabel>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

function RetroInput({ value, onChange, type = 'text', placeholder, disabled, className = '' }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className={`w-full px-3 py-2 rounded-lg text-sm transition-all outline-none disabled:opacity-50 ${className}`}
      style={{
        fontFamily: 'Inter, sans-serif',
        border: '2px solid rgba(0,104,183,0.3)',
        backgroundColor: '#ffffff',
      }}
      onFocus={(e) => {
        e.currentTarget.style.border = '2px solid #0068B7';
        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0,104,183,0.15)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.border = '2px solid rgba(0,104,183,0.3)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    />
  );
}

function RetroSelect({ value, onChange, disabled, children, className = '' }) {
  return (
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`w-full px-3 py-2 rounded-lg text-sm transition-all outline-none disabled:opacity-50 appearance-none ${className}`}
      style={{
        fontFamily: 'Inter, sans-serif',
        border: '2px solid rgba(0,104,183,0.3)',
        backgroundColor: '#ffffff',
      }}
      onFocus={(e) => {
        e.currentTarget.style.border = '2px solid #0068B7';
        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0,104,183,0.15)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.border = '2px solid rgba(0,104,183,0.3)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {children}
    </select>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label className="block mb-1.5">
        <SectionLabel>{label}</SectionLabel>
      </label>
      {children}
    </div>
  );
}

function QuestModal({ isOpen, onClose, title, subtitle, size = 'lg', children }) {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const maxWidth = size === 'lg' ? 'max-w-2xl' : size === 'xl' ? 'max-w-3xl' : 'max-w-md';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`${maxWidth} w-full max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <QuestWindow padding="p-0">
          {/* ヘッダー */}
          <div
            className="flex items-center justify-between px-6 py-4"
            style={{ borderBottom: '2px solid rgba(0,104,183,0.2)' }}
          >
            <div>
              <h2 style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: '18px', color: '#0068B7' }}>
                {title}
              </h2>
              {subtitle && <SectionLabel>{subtitle}</SectionLabel>}
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-blue-50 transition-colors text-[#717782] hover:text-[#0068B7]"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>close</span>
            </button>
          </div>
          {/* コンテンツ */}
          <div className="px-6 py-5">{children}</div>
        </QuestWindow>
      </div>
    </div>
  );
}

const AIRCRAFT_TABS = [
  { id: 'basic',   label: '基本情報',  sub: 'Basic Info',      icon: 'info' },
  { id: 'rid',     label: 'リモートID', sub: 'Remote ID',       icon: 'wifi' },
  { id: 'spec',    label: 'スペック',   sub: 'Specifications',  icon: 'straighten' },
  { id: 'raw',     label: '生データ',   sub: 'Raw JSON',        icon: 'data_object' },
];

function AircraftBottomSheet({ isOpen, onClose, aircraft }) {
  const [shown, setShown] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    if (isOpen) {
      setActiveTab('basic');
      const raf = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(raf);
    } else {
      setShown(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!aircraft) return null;

  const info = getAircraftInfo(aircraft);

  return (
    <div
      className="fixed inset-0 z-50"
      style={{ pointerEvents: isOpen ? 'all' : 'none' }}
    >
      {/* オーバーレイ */}
      <div
        className="absolute inset-0 bg-black transition-opacity duration-300"
        style={{ opacity: shown ? 0.5 : 0 }}
        onClick={onClose}
      />

      {/* ボトムシート本体 */}
      <div
        className="absolute bottom-0 w-full max-w-3xl flex flex-col border-t-4 border-l-4 border-r-4 border-[#0068B7] rounded-t-2xl bg-white transition-transform duration-500 ease-out"
        style={{
          left: '50%',
          transform: shown ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(100%)',
          boxShadow: '0 -8px 0 0 #00508e',
        }}
      >
        {/* 内側装飾ボーダー */}
        <div className="absolute top-2 left-2 right-2 bottom-0 border-t-2 border-l-2 border-r-2 border-[#0068B7]/20 rounded-t-xl pointer-events-none" />

        {/* ドラッグハンドル */}
        <div className="relative z-10 flex justify-center pt-3 pb-1 cursor-pointer" onClick={onClose}>
          <div className="w-10 h-1.5 rounded-full bg-[#0068B7]/30 hover:bg-[#0068B7]/60 transition-colors" />
        </div>

        {/* ヘッダー */}
        <div
          className="relative z-10 flex items-center justify-between px-6 py-3 bg-white flex-shrink-0"
          style={{ borderBottom: '2px solid rgba(0,104,183,0.2)' }}
        >
          <div className="flex items-center gap-3">
            <span
              className="material-symbols-outlined text-[#0068B7]"
              style={{ fontSize: '20px', fontVariationSettings: "'FILL' 1" }}
            >
              flight
            </span>
            <div>
              <h2 style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: '17px', color: '#0068B7' }}>
                機体詳細
              </h2>
              <SectionLabel>{info?.registration_code || 'Aircraft Detail'}</SectionLabel>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-blue-50 transition-colors text-[#717782] hover:text-[#0068B7]"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>close</span>
          </button>
        </div>

        {/* タブバー */}
        <div
          className="relative z-10 flex gap-1.5 px-4 py-2.5 flex-shrink-0 overflow-x-auto"
          style={{ borderBottom: '2px solid rgba(0,104,183,0.15)' }}
        >
          {AIRCRAFT_TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0"
                style={{
                  fontFamily: 'Space Grotesk, sans-serif',
                  fontWeight: 700,
                  ...(isActive
                    ? { backgroundColor: '#0068B7', color: '#ffffff', boxShadow: '0 3px 0 0 #00508e' }
                    : { color: '#717782', backgroundColor: 'transparent' }),
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: '13px', fontVariationSettings: "'FILL' 1" }}
                >
                  {tab.icon}
                </span>
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* タブコンテンツ
            全タブを同一 grid セル（gridArea: '1/1'）に重ねて描画することで、
            セル高さ＝最も背の高いタブに自動的に揃う。
            非アクティブタブは visibility: hidden で非表示（DOM上は存在し高さに寄与）。 */}
        <div className="relative z-10 px-6 py-5" style={{ display: 'grid' }}>
          {/* 基本情報 */}
          <div
            style={{
              gridArea: '1/1',
              visibility: activeTab === 'basic' ? 'visible' : 'hidden',
              pointerEvents: activeTab === 'basic' ? 'auto' : 'none',
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <DetailRow label="登録記号" value={info.registration_code} />
              <DetailRow label="製造番号" value={info.manufacturing_number} />
              <DetailRow label="機体種類" value={AIRCRAFT_TYPE_LABELS[info.aircraft_type] || info.aircraft_type} />
              <DetailRow label="メーカー" value={info.manufacturer_jpn || info.manufacturer_eng} />
              <DetailRow label="モデル" value={info.model_jpn || info.model_eng} />
              <DetailRow label="登録日" value={formatDate(info.effectiveness_period_self)} />
              <DetailRow label="有効期限" value={formatDate(info.effectiveness_period_to)} />
              <DetailRow label="最終更新日" value={formatDate(info.last_update_date)} />
              <DetailRow
                label="ステータス"
                value={
                  <StatusBadge status={info.aircraft_status === '1' ? 'active' : info.aircraft_status === '2' ? 'expired' : 'deleted'} />
                }
              />
            </div>
          </div>

          {/* リモートID */}
          <div
            style={{
              gridArea: '1/1',
              visibility: activeTab === 'rid' ? 'visible' : 'hidden',
              pointerEvents: activeTab === 'rid' ? 'auto' : 'none',
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <DetailRow label="RID製造番号" value={info.rid_manufacturing_number} />
              <DetailRow label="RIDメーカー" value={info.rid_manufacturer_jpn || info.rid_manufacturer_eng} />
              <DetailRow label="RID型式" value={info.rid_model_jpn || info.rid_model_eng} />
              <DetailRow label="RID種類" value={info.rid_type === '1' ? '内蔵型' : info.rid_type === '2' ? '外付け型' : info.rid_type} />
            </div>
          </div>

          {/* スペック */}
          <div
            style={{
              gridArea: '1/1',
              visibility: activeTab === 'spec' ? 'visible' : 'hidden',
              pointerEvents: activeTab === 'spec' ? 'auto' : 'none',
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <DetailRow label="機体重量" value={info.aircraft_weight ? `${info.aircraft_weight} kg` : '—'} />
              <DetailRow label="最大離陸重量" value={info.maximum_takeoff_weight ? `${info.maximum_takeoff_weight} kg` : '—'} />
              <DetailRow label="全長" value={info.aircraft_length ? `${info.aircraft_length} m` : '—'} />
              <DetailRow label="全幅" value={info.aircraft_width ? `${info.aircraft_width} m` : '—'} />
              <DetailRow label="全高" value={info.aircraft_height ? `${info.aircraft_height} m` : '—'} />
            </div>
          </div>

          {/* 生データ */}
          <div
            style={{
              gridArea: '1/1',
              visibility: activeTab === 'raw' ? 'visible' : 'hidden',
              pointerEvents: activeTab === 'raw' ? 'auto' : 'none',
            }}
          >
            <div
              className="rounded-xl p-4 overflow-auto"
              style={{ border: '2px solid rgba(0,104,183,0.2)', backgroundColor: 'rgba(0,104,183,0.02)', maxHeight: '240px' }}
            >
              <pre className="text-xs whitespace-pre-wrap" style={{ fontFamily: 'monospace', color: '#414751' }}>
                {JSON.stringify(aircraft, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== データ定数 =====

const AIRCRAFT_TYPE_LABELS = {
  '1': '回転翼（マルチローター）',
  '2': '回転翼（ヘリコプター）',
  '3': '固定翼',
  '4': '固定翼（VTOL）',
};

const OWNER_CLASSIFICATION_LABELS = {
  '1': '個人',
  '9': '法人',
};

const getAircraftInfo = (aircraft) => aircraft?.aircraft_information || aircraft;
const getOwnerInfo = (aircraft) => aircraft?.owner_information;
const getUserInfo = (aircraft) => aircraft?.user_information;

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('ja-JP');
  } catch {
    return dateStr;
  }
};

// ===== メインコンポーネント =====

export default function DipsAircrafts() {
  const [dipsAircrafts, setDipsAircrafts] = useState(null);
  const [dipsLoading, setDipsLoading] = useState(false);
  const [dipsError, setDipsError] = useState(null);
  const [dipsAuthenticated, setDipsAuthenticated] = useState(dipsApi.isAuthenticated());
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [isAircraftSheetOpen, setIsAircraftSheetOpen] = useState(false);

  const openAircraftSheet = (aircraft) => {
    setSelectedAircraft(aircraft);
    setIsAircraftSheetOpen(true);
  };

  const closeAircraftSheet = () => {
    setIsAircraftSheetOpen(false);
    setTimeout(() => setSelectedAircraft(null), 300);
  };

  // 飛行計画
  const [flightPlans, setFlightPlans] = useState([]);
  const [flightPlansLoading, setFlightPlansLoading] = useState(false);
  const [flightPlansError, setFlightPlansError] = useState(null);
  const [selectedFlightPlan, setSelectedFlightPlan] = useState(null);
  const [flightPlanAircraft, setFlightPlanAircraft] = useState(null);
  const [flightPlanAuthenticated, setFlightPlanAuthenticated] = useState(dipsApi.isFlightPlanAuthenticated());
  const [flightPlanAuthLoading, setFlightPlanAuthLoading] = useState(false);
  const [searchDate, setSearchDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [scanMode, setScanMode] = useState(false);
  const [scanProgress, setScanProgress] = useState('');
  const [scanResults, setScanResults] = useState([]);

  // 飛行計画登録フォーム
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportResult, setReportResult] = useState(null);
  const [reportForm, setReportForm] = useState({
    flightDate: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '17:00',
    latitude: '35.6895',
    longitude: '139.6917',
    radius: '500',
    purpose: '空撮',
    maxAltitude: '150',
    remarks: '',
  });

  // DIPS機体情報取得
  const fetchDipsAircrafts = useCallback(async () => {
    if (!dipsApi.isAuthenticated()) {
      setDipsAuthenticated(false);
      return;
    }
    setDipsLoading(true);
    setDipsError(null);
    try {
      const data = await dipsApi.getAircrafts();
      setDipsAircrafts(data);
      setDipsAuthenticated(true);
    } catch (err) {
      setDipsError(err.message);
      if (err.message.includes('認証')) {
        setDipsAuthenticated(false);
      }
    } finally {
      setDipsLoading(false);
    }
  }, []);

  const formatDipsDateTime = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${y}${m}${d} ${h}${min}`;
  };

  // DIPS飛行計画取得
  const fetchFlightPlans = useCallback(async () => {
    if (!dipsApi.isFlightPlanAuthenticated()) return;

    setFlightPlansLoading(true);
    setFlightPlansError(null);
    try {
      const [year, month, day] = searchDate.split('-').map(Number);
      const startTime = new Date(year, month - 1, day, 0, 0);
      const finishTime = new Date(year, month - 1, day, 23, 59);

      const searchParams = {
        features: {
          type: 'Circle',
          center: [139.6917, 35.6895],
          radius: 500000,
        },
        startTime: formatDipsDateTime(startTime),
        finishTime: formatDipsDateTime(finishTime),
        allFlightPlan: '1',
      };

      const data = await dipsApi.getFlightPlans(searchParams);
      const plans = Array.isArray(data) ? data : data.flight_plans || data.flightPlans || data.items || data.results || [];
      setFlightPlans(plans);
    } catch (err) {
      setFlightPlansError(err.message);
    } finally {
      setFlightPlansLoading(false);
    }
  }, [searchDate]);

  // DIPS認証開始
  const handleDipsAuth = async () => {
    setDipsLoading(true);
    setDipsError(null);
    try {
      const result = await dipsApi.getAuthUrl('aircraft');
      const { authUrl } = result;
      if (!authUrl) throw new Error('認証URLが取得できませんでした');

      const handleMessage = (event) => {
        if (event.data?.type === 'DIPS_AUTH_SUCCESS') {
          dipsApi.storeToken({
            accessToken: event.data.accessToken,
            refreshToken: event.data.refreshToken,
            expiresIn: event.data.expiresIn,
          });
          setDipsAuthenticated(true);
          setDipsLoading(false);
          fetchDipsAircrafts();
          window.removeEventListener('message', handleMessage);
        } else if (event.data?.type === 'DIPS_AUTH_ERROR') {
          setDipsError('認証エラー: ' + event.data.error);
          setDipsLoading(false);
          window.removeEventListener('message', handleMessage);
        }
      };
      window.addEventListener('message', handleMessage);

      const authWindow = window.open(authUrl, 'dips_auth', 'width=600,height=700');
      if (!authWindow) {
        window.removeEventListener('message', handleMessage);
        throw new Error('ポップアップがブロックされました。ポップアップを許可してください。');
      }

      const checkAuth = setInterval(() => {
        try {
          if (authWindow?.closed) {
            clearInterval(checkAuth);
            setTimeout(() => {
              window.removeEventListener('message', handleMessage);
              if (dipsApi.isAuthenticated()) {
                setDipsAuthenticated(true);
                setDipsLoading(false);
                fetchDipsAircrafts();
              } else {
                setDipsLoading(false);
              }
            }, 1000);
          }
        } catch { /* Cross-origin エラーは無視 */ }
      }, 500);
    } catch (err) {
      setDipsError('認証エラー: ' + err.message);
      setDipsLoading(false);
    }
  };

  useEffect(() => {
    if (dipsAuthenticated && !dipsAircrafts) {
      fetchDipsAircrafts();
    }
  }, [dipsAuthenticated, dipsAircrafts, fetchDipsAircrafts]);

  useEffect(() => {
    if (flightPlanAuthenticated && !scanMode) {
      fetchFlightPlans();
    }
  }, [flightPlanAuthenticated, searchDate, scanMode, fetchFlightPlans]);

  // 範囲スキャン
  const scanDateRange = async () => {
    if (!dipsApi.isFlightPlanAuthenticated()) {
      setFlightPlansError('飛行計画認証が必要です');
      return;
    }

    setScanMode(true);
    setScanResults([]);
    setFlightPlans([]);
    setFlightPlansError(null);

    const today = new Date();
    const results = [];
    const startOffset = -14;
    const endOffset = 60;

    for (let i = startOffset; i <= endOffset; i++) {
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + i);
      const dateStr = targetDate.toISOString().split('T')[0];
      setScanProgress(`${dateStr} (${i - startOffset + 1}/${endOffset - startOffset + 1})`);

      try {
        const startTime = new Date(targetDate);
        startTime.setHours(0, 0, 0, 0);
        const finishTime = new Date(targetDate);
        finishTime.setHours(23, 59, 0, 0);

        const searchParams = {
          features: { type: 'Circle', center: [139.6917, 35.6895], radius: 500000 },
          startTime: formatDipsDateTime(startTime),
          finishTime: formatDipsDateTime(finishTime),
          allFlightPlan: '1',
        };

        const data = await dipsApi.getFlightPlans(searchParams);
        const plans = Array.isArray(data) ? data : data.flight_plans || data.flightPlans || data.items || data.results || [];

        if (plans.length > 0) {
          results.push({ date: dateStr, count: plans.length, plans });
        }
      } catch { /* エラーは無視して続行 */ }

      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setScanProgress('');
    setScanResults(results);
    setScanMode(false);
  };

  const handleShowFlightPlans = (aircraft) => {
    setFlightPlanAircraft(aircraft);
  };

  // 飛行計画認証開始
  const handleFlightPlanAuth = async () => {
    setFlightPlanAuthLoading(true);
    setFlightPlansError(null);
    try {
      const result = await dipsApi.getAuthUrl('flightPlan');
      const { authUrl } = result;
      if (!authUrl) throw new Error('認証URLが取得できませんでした');

      const handleMessage = (event) => {
        if (event.data?.type === 'DIPS_AUTH_SUCCESS') {
          dipsApi.storeFlightPlanToken({
            accessToken: event.data.accessToken,
            refreshToken: event.data.refreshToken,
            expiresIn: event.data.expiresIn,
          });
          setFlightPlanAuthenticated(true);
          setFlightPlanAuthLoading(false);
          window.removeEventListener('message', handleMessage);
        } else if (event.data?.type === 'DIPS_AUTH_ERROR') {
          setFlightPlansError('認証エラー: ' + event.data.error);
          setFlightPlanAuthLoading(false);
          window.removeEventListener('message', handleMessage);
        }
      };
      window.addEventListener('message', handleMessage);

      const authWindow = window.open(authUrl, 'dips_flightplan_auth', 'width=600,height=700');
      if (!authWindow) {
        window.removeEventListener('message', handleMessage);
        throw new Error('ポップアップがブロックされました。');
      }

      const checkAuth = setInterval(() => {
        try {
          if (authWindow?.closed) {
            clearInterval(checkAuth);
            setTimeout(() => {
              window.removeEventListener('message', handleMessage);
              if (dipsApi.isFlightPlanAuthenticated()) {
                setFlightPlanAuthenticated(true);
                setFlightPlanAuthLoading(false);
              } else {
                setFlightPlanAuthLoading(false);
              }
            }, 1000);
          }
        } catch { /* Cross-origin エラーは無視 */ }
      }, 500);
    } catch (err) {
      setFlightPlansError('認証エラー: ' + err.message);
      setFlightPlanAuthLoading(false);
    }
  };

  // 飛行計画通報（登録）
  const handleReportFlightPlan = async () => {
    if (!dipsApi.isFlightPlanAuthenticated()) {
      setFlightPlansError('飛行計画認証が必要です');
      return;
    }

    setReportLoading(true);
    setReportResult(null);
    setFlightPlansError(null);

    try {
      const [year, month, day] = reportForm.flightDate.split('-').map(Number);
      const [startH, startM] = reportForm.startTime.split(':').map(Number);
      const [endH, endM] = reportForm.endTime.split(':').map(Number);

      const startDateTime = new Date(year, month - 1, day, startH, startM);
      const endDateTime = new Date(year, month - 1, day, endH, endM);

      const reportData = {
        features: {
          type: 'Circle',
          center: [parseFloat(reportForm.longitude), parseFloat(reportForm.latitude)],
          radius: parseInt(reportForm.radius, 10),
        },
        startTime: formatDipsDateTime(startDateTime),
        finishTime: formatDipsDateTime(endDateTime),
        flightPurpose: reportForm.purpose,
        maxAltitude: parseInt(reportForm.maxAltitude, 10),
        remarks: reportForm.remarks || undefined,
      };

      const result = await dipsApi.reportFlightPlan(reportData);
      setReportResult({ success: true, data: result });
      fetchFlightPlans();
    } catch (err) {
      setReportResult({ success: false, error: err.message });
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* ページヘッダー */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span
            className="material-symbols-outlined text-[#0068B7]"
            style={{ fontSize: '32px', fontVariationSettings: "'FILL' 1", transform: 'rotate(-45deg)', display: 'inline-block' }}
          >
            link
          </span>
          <div>
            <h1
              className="text-2xl tracking-tight text-[#0068B7]"
              style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700 }}
            >
              DIPS2.0 連携
            </h1>
            <SectionLabel>DIPS Aircraft Registry</SectionLabel>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dipsAuthenticated ? (
            <>
              <StatusBadge status="linked" />
              <AeroButton variant="secondary" size="sm" onClick={fetchDipsAircrafts} disabled={dipsLoading}>
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>refresh</span>
                同期
              </AeroButton>
              <AeroButton
                variant="secondary"
                size="sm"
                onClick={() => {
                  dipsApi.clearToken();
                  setDipsAuthenticated(false);
                  setDipsAircrafts(null);
                  const logoutWindow = window.open(
                    'https://www.dips-regdev.mlit.go.jp/auth/realms/drs-utm/protocol/openid-connect/logout',
                    'dips_logout',
                    'width=500,height=400'
                  );
                  if (logoutWindow) setTimeout(() => logoutWindow.close(), 500);
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>logout</span>
                ログアウト
              </AeroButton>
            </>
          ) : (
            <AeroButton onClick={handleDipsAuth} isLoading={dipsLoading} size="lg">
              <span className="material-symbols-outlined" style={{ fontSize: '20px', transform: 'rotate(-45deg)', display: 'inline-block' }}>link</span>
              {dipsLoading ? '接続中...' : 'DIPS2.0認証'}
            </AeroButton>
          )}
        </div>
      </div>

      {/* エラー表示 */}
      <ErrorAlert message={dipsError} />

      {/* 未認証状態 */}
      {!dipsAuthenticated && !dipsError && !dipsLoading && (
        <QuestWindow>
          <div className="flex flex-col items-center text-center py-8">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
              style={{ backgroundColor: '#0068b7', boxShadow: '0 5px 0 0 #00508e' }}
            >
              <span
                className="material-symbols-outlined text-white"
                style={{ fontSize: '40px', fontVariationSettings: "'FILL' 1", transform: 'rotate(-45deg)', display: 'inline-block' }}
              >
                link
              </span>
            </div>
            <h2
              className="text-2xl tracking-tight text-[#0068B7] mb-2"
              style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700 }}
            >
              DIPS2.0と連携する
            </h2>
            <div className="h-0.5 w-16 mb-5 border-b-2 border-[#0068B7]/40" />
            <p
              className="max-w-md mb-8"
              style={{ fontFamily: 'Inter, sans-serif', lineHeight: '1.6', color: '#414751' }}
            >
              国土交通省のドローン情報基盤システム（DIPS2.0）と連携すると、
              登録されている機体情報を表示できます。
            </p>
            <AeroButton onClick={handleDipsAuth} isLoading={dipsLoading} size="lg">
              <span
                className="material-symbols-outlined"
                style={{ fontSize: '22px', fontVariationSettings: "'FILL' 1", transform: 'rotate(-45deg)', display: 'inline-block' }}
              >
                link
              </span>
              DIPS2.0認証を開始
            </AeroButton>
          </div>
        </QuestWindow>
      )}

      {/* ローディング */}
      {dipsLoading && (
        <QuestWindow>
          <div className="flex flex-col items-center justify-center py-12 gap-5">
            <Spinner size="lg" />
            <div className="text-center">
              <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600, color: '#0068B7' }}>
                機体情報を取得中...
              </p>
              <p className="text-sm mt-1" style={{ fontFamily: 'Inter, sans-serif', color: '#717782' }}>
                DIPS2.0と通信しています
              </p>
            </div>
          </div>
        </QuestWindow>
      )}

      {/* 所有者・使用者情報 */}
      {dipsAuthenticated && !dipsLoading && dipsAircrafts?.length > 0 && (() => {
        const firstAircraft = dipsAircrafts[0];
        const owner = getOwnerInfo(firstAircraft);
        const user = getUserInfo(firstAircraft);
        const isUserDifferent = user && user.owner_user_same_confirmation === '2';

        if (!owner && !isUserDifferent) return null;

        return (
          <div className={`grid gap-6 ${isUserDifferent ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
            {owner && (
              <QuestWindow>
                <SectionHeader
                  icon="person"
                  title="所有者情報"
                  subtitle={`Owner · ${OWNER_CLASSIFICATION_LABELS[owner.owner_classification] || owner.owner_classification}`}
                />
                <div className="grid grid-cols-2 gap-3">
                  <DetailRow label="氏名 / 法人名" value={owner.owner_fullname || owner.owner_corporation_name} />
                  <DetailRow label="フリガナ" value={owner.owner_furigana} />
                  <DetailRow label="住所" value={owner.owner_address} />
                  <DetailRow label="電話番号" value={owner.owner_tel} />
                  <DetailRow label="メールアドレス" value={owner.owner_email_address} />
                  {owner.owner_birthday && <DetailRow label="生年月日" value={owner.owner_birthday} />}
                </div>
              </QuestWindow>
            )}
            {isUserDifferent && (
              <QuestWindow>
                <SectionHeader
                  icon="group"
                  title="使用者情報"
                  subtitle={`User · ${OWNER_CLASSIFICATION_LABELS[user.user_classification] || user.user_classification}`}
                />
                <div className="grid grid-cols-2 gap-3">
                  <DetailRow label="氏名 / 法人名" value={user.user_fullname || user.user_corporation_name} />
                  <DetailRow label="フリガナ" value={user.user_furigana} />
                  <DetailRow label="住所" value={user.user_address} />
                  <DetailRow label="電話番号" value={user.user_tel} />
                  <DetailRow label="メールアドレス" value={user.user_email_address} />
                  {user.user_corporation_representative_name && (
                    <DetailRow label="代表者名" value={user.user_corporation_representative_name} />
                  )}
                  {user.user_department_name && (
                    <DetailRow label="部署名" value={user.user_department_name} />
                  )}
                </div>
              </QuestWindow>
            )}
          </div>
        );
      })()}

      {/* 登録機体一覧 */}
      {dipsAuthenticated && !dipsLoading && (() => {
        const sortedAircrafts = dipsAircrafts ? [...dipsAircrafts].sort((a, b) => {
          const statusA = getAircraftInfo(a)?.aircraft_status;
          const statusB = getAircraftInfo(b)?.aircraft_status;
          if (statusA === '1' && statusB !== '1') return -1;
          if (statusA !== '1' && statusB === '1') return 1;
          return 0;
        }) : [];

        const activeCount = sortedAircrafts.filter(a => getAircraftInfo(a)?.aircraft_status === '1').length;
        const inactiveCount = sortedAircrafts.length - activeCount;

        return (
          <QuestWindow>
            <SectionHeader
              icon="flight"
              title="登録機体一覧"
              subtitle={`Aircraft Registry · ${sortedAircrafts.length} 機`}
              actions={
                <div className="flex items-center gap-2">
                  {activeCount > 0 && <StatusBadge status="active" />}
                  {inactiveCount > 0 && <StatusBadge status="deleted" />}
                </div>
              }
            />
            {sortedAircrafts.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {sortedAircrafts.map((aircraft, idx) => {
                  const info = getAircraftInfo(aircraft);
                  const isActive = info.aircraft_status === '1';
                  const isExpired = info.aircraft_status === '2';
                  const badgeStatus = isActive ? 'active' : isExpired ? 'expired' : 'deleted';

                  return (
                    <div
                      key={idx}
                      className="p-4 rounded-xl border-2 bg-white cursor-pointer transition-all"
                      style={{
                        borderColor: isActive ? 'rgba(0,104,183,0.3)' : 'rgba(113,119,130,0.3)',
                        boxShadow: isActive ? '0 4px 0 0 rgba(0,80,142,0.2)' : '0 4px 0 0 rgba(113,119,130,0.2)',
                        opacity: isActive ? 1 : 0.75,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = isActive ? '#0068B7' : '#717782';
                        e.currentTarget.style.boxShadow = isActive ? '0 6px 0 0 rgba(0,80,142,0.4)' : '0 6px 0 0 rgba(113,119,130,0.4)';
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = isActive ? 'rgba(0,104,183,0.3)' : 'rgba(113,119,130,0.3)';
                        e.currentTarget.style.boxShadow = isActive ? '0 4px 0 0 rgba(0,80,142,0.2)' : '0 4px 0 0 rgba(113,119,130,0.2)';
                        e.currentTarget.style.opacity = isActive ? '1' : '0.75';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                      onClick={() => openAircraftSheet(aircraft)}
                    >
                      {/* 登録記号とステータス */}
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: '16px', color: isActive ? '#0068B7' : '#717782' }}>
                            {info.registration_code || '—'}
                          </div>
                          <div className="text-sm mt-0.5" style={{ fontFamily: 'Inter, sans-serif', color: '#717782' }}>
                            {info.manufacturer_jpn || info.manufacturer_eng} / {info.model_jpn || info.model_eng}
                          </div>
                        </div>
                        <StatusBadge status={badgeStatus} />
                      </div>

                      {/* スペック */}
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <SectionLabel>製造番号</SectionLabel>
                            <div style={{ fontFamily: 'Inter, sans-serif', color: '#191c21' }}>{info.manufacturing_number || '—'}</div>
                          </div>
                          <div>
                            <SectionLabel>機体種類</SectionLabel>
                            <div style={{ fontFamily: 'Inter, sans-serif', color: '#191c21' }}>
                              {AIRCRAFT_TYPE_LABELS[info.aircraft_type] || info.aircraft_type || '—'}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <SectionLabel>登録日</SectionLabel>
                            <div style={{ fontFamily: 'Inter, sans-serif', color: '#191c21' }}>{formatDate(info.effectiveness_period_self)}</div>
                          </div>
                          <div>
                            <SectionLabel>有効期限</SectionLabel>
                            <div style={{ fontFamily: 'Inter, sans-serif', color: isExpired ? '#DC2626' : '#191c21' }}>
                              {formatDate(info.effectiveness_period_to)}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <SectionLabel>重量</SectionLabel>
                            <div style={{ fontFamily: 'Inter, sans-serif', color: '#191c21' }}>{info.aircraft_weight ? `${info.aircraft_weight} kg` : '—'}</div>
                          </div>
                          <div>
                            <SectionLabel>最大離陸重量</SectionLabel>
                            <div style={{ fontFamily: 'Inter, sans-serif', color: '#191c21' }}>{info.maximum_takeoff_weight ? `${info.maximum_takeoff_weight} kg` : '—'}</div>
                          </div>
                        </div>
                      </div>

                      {/* アクション */}
                      <div className="mt-3 pt-3 flex justify-between items-center" style={{ borderTop: '2px dashed rgba(0,104,183,0.2)' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowFlightPlans(aircraft);
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold transition-all"
                          style={{
                            fontFamily: 'Space Grotesk, sans-serif',
                            color: '#7f3b00',
                            border: '2px solid rgba(164,78,1,0.3)',
                            backgroundColor: 'transparent',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(164,78,1,0.08)'; e.currentTarget.style.borderColor = '#a44e01'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(164,78,1,0.3)'; }}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: '12px' }}>calendar_month</span>
                          飛行計画
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); openAircraftSheet(aircraft); }}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold transition-all"
                          style={{
                            fontFamily: 'Space Grotesk, sans-serif',
                            color: '#0068B7',
                            border: '2px solid rgba(0,104,183,0.3)',
                            backgroundColor: 'transparent',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,104,183,0.08)'; e.currentTarget.style.borderColor = '#0068B7'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(0,104,183,0.3)'; }}
                        >
                          詳細
                          <span className="material-symbols-outlined" style={{ fontSize: '12px' }}>chevron_right</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center py-12" style={{ color: '#717782' }}>
                <span className="material-symbols-outlined mb-4" style={{ fontSize: '48px', opacity: 0.3 }}>flight</span>
                <p style={{ fontFamily: 'Inter, sans-serif' }}>DIPS2.0に登録されている機体はありません</p>
              </div>
            )}
          </QuestWindow>
        );
      })()}

      {/* 飛行計画セクション */}
      {dipsAuthenticated && !dipsLoading && (
        <QuestWindow>
          <SectionHeader
            icon="map"
            title="飛行計画"
            subtitle={`Flight Plan · ${flightPlanAuthenticated ? '認証済み' : '要認証'}`}
            actions={
              <div className="flex items-center gap-2">
                {flightPlanAuthenticated ? (
                  <>
                    <RetroInput
                      type="date"
                      value={searchDate}
                      onChange={(e) => setSearchDate(e.target.value)}
                      disabled={scanMode}
                      className="w-40"
                    />
                    <AeroButton
                      variant="secondary"
                      size="sm"
                      onClick={() => { setScanResults([]); fetchFlightPlans(); }}
                      disabled={flightPlansLoading || scanMode}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>search</span>
                      検索
                    </AeroButton>
                    <AeroButton
                      variant="primary"
                      size="sm"
                      onClick={scanDateRange}
                      disabled={flightPlansLoading || scanMode}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>radar</span>
                      {scanMode ? '検索中...' : '範囲スキャン'}
                    </AeroButton>
                    <AeroButton
                      variant="success"
                      size="sm"
                      onClick={() => setShowReportForm(!showReportForm)}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>add</span>
                      飛行計画通報
                    </AeroButton>
                  </>
                ) : (
                  <AeroButton
                    variant="primary"
                    size="sm"
                    onClick={handleFlightPlanAuth}
                    isLoading={flightPlanAuthLoading}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '14px', transform: 'rotate(-45deg)', display: 'inline-block' }}>link</span>
                    {flightPlanAuthLoading ? '認証中...' : '飛行計画認証'}
                  </AeroButton>
                )}
              </div>
            }
          />

          {/* 飛行計画通報フォーム */}
          {showReportForm && flightPlanAuthenticated && (
            <div
              className="mb-6 p-5 rounded-xl"
              style={{ border: '2px solid rgba(0,104,183,0.3)', backgroundColor: 'rgba(0,104,183,0.03)' }}
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="material-symbols-outlined text-[#0068B7]" style={{ fontSize: '18px', fontVariationSettings: "'FILL' 1" }}>edit_note</span>
                <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: '14px', color: '#0068B7' }}>
                  飛行計画通報（新規登録）
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FormField label="飛行日">
                  <RetroInput
                    type="date"
                    value={reportForm.flightDate}
                    onChange={(e) => setReportForm({ ...reportForm, flightDate: e.target.value })}
                  />
                </FormField>
                <FormField label="開始時刻">
                  <RetroInput
                    type="time"
                    value={reportForm.startTime}
                    onChange={(e) => setReportForm({ ...reportForm, startTime: e.target.value })}
                  />
                </FormField>
                <FormField label="終了時刻">
                  <RetroInput
                    type="time"
                    value={reportForm.endTime}
                    onChange={(e) => setReportForm({ ...reportForm, endTime: e.target.value })}
                  />
                </FormField>
                <FormField label="緯度">
                  <RetroInput
                    value={reportForm.latitude}
                    onChange={(e) => setReportForm({ ...reportForm, latitude: e.target.value })}
                    placeholder="例: 35.6895"
                  />
                </FormField>
                <FormField label="経度">
                  <RetroInput
                    value={reportForm.longitude}
                    onChange={(e) => setReportForm({ ...reportForm, longitude: e.target.value })}
                    placeholder="例: 139.6917"
                  />
                </FormField>
                <FormField label="半径 (m)">
                  <RetroInput
                    type="number"
                    value={reportForm.radius}
                    onChange={(e) => setReportForm({ ...reportForm, radius: e.target.value })}
                    placeholder="例: 500"
                  />
                </FormField>
                <FormField label="飛行目的">
                  <RetroSelect
                    value={reportForm.purpose}
                    onChange={(e) => setReportForm({ ...reportForm, purpose: e.target.value })}
                  >
                    <option value="空撮">空撮</option>
                    <option value="測量">測量</option>
                    <option value="点検">点検</option>
                    <option value="農薬散布">農薬散布</option>
                    <option value="物資輸送">物資輸送</option>
                    <option value="趣味">趣味</option>
                    <option value="その他">その他</option>
                  </RetroSelect>
                </FormField>
                <FormField label="最大高度 (m)">
                  <RetroInput
                    type="number"
                    value={reportForm.maxAltitude}
                    onChange={(e) => setReportForm({ ...reportForm, maxAltitude: e.target.value })}
                    placeholder="例: 150"
                  />
                </FormField>
                <FormField label="備考">
                  <RetroInput
                    value={reportForm.remarks}
                    onChange={(e) => setReportForm({ ...reportForm, remarks: e.target.value })}
                    placeholder="任意"
                  />
                </FormField>
              </div>

              <div className="mt-5 flex items-center gap-3">
                <AeroButton variant="primary" onClick={handleReportFlightPlan} isLoading={reportLoading}>
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontVariationSettings: "'FILL' 1" }}>send</span>
                  {reportLoading ? '送信中...' : '飛行計画を通報'}
                </AeroButton>
                <AeroButton variant="secondary" onClick={() => { setShowReportForm(false); setReportResult(null); }}>
                  キャンセル
                </AeroButton>
              </div>

              {reportResult && (
                <div className="mt-4">
                  {reportResult.success ? (
                    <SuccessAlert message="飛行計画を通報しました" />
                  ) : (
                    <ErrorAlert message={`エラー: ${reportResult.error}`} />
                  )}
                </div>
              )}
            </div>
          )}

          {/* 飛行計画コンテンツ */}
          {!flightPlanAuthenticated ? (
            <div className="flex flex-col items-center py-12 text-center">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: 'rgba(113,119,130,0.1)', border: '2px solid rgba(113,119,130,0.3)' }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '32px', color: '#717782', fontVariationSettings: "'FILL' 1" }}>lock</span>
              </div>
              <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600, color: '#414751' }}>
                飛行計画の表示には別途認証が必要です
              </p>
              <p className="text-sm mt-1" style={{ fontFamily: 'Inter, sans-serif', color: '#717782' }}>
                上の「飛行計画認証」ボタンを押してください
              </p>
            </div>
          ) : scanMode ? (
            <div className="flex flex-col items-center justify-center py-10 gap-4">
              <Spinner size="lg" />
              <div className="text-center">
                <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600, color: '#0068B7' }}>
                  スキャン中...
                </p>
                <p className="text-sm mt-1" style={{ fontFamily: 'Inter, sans-serif', color: '#717782' }}>
                  {scanProgress}
                </p>
                <p className="text-xs mt-0.5" style={{ fontFamily: 'Inter, sans-serif', color: '#717782' }}>
                  過去2週間〜未来2ヶ月を検索中
                </p>
              </div>
            </div>
          ) : flightPlansLoading ? (
            <div className="flex items-center justify-center py-10 gap-3">
              <Spinner />
              <span style={{ fontFamily: 'Inter, sans-serif', color: '#717782' }}>飛行計画を取得中...</span>
            </div>
          ) : flightPlansError ? (
            <div className="py-4">
              <ErrorAlert message={`飛行計画の取得に失敗しました: ${flightPlansError}`} />
              <p className="text-sm mt-2" style={{ fontFamily: 'Inter, sans-serif', color: '#717782' }}>
                飛行計画APIには別途認証が必要な場合があります
              </p>
            </div>
          ) : scanResults.length > 0 ? (
            // スキャン結果
            <div className="space-y-3">
              <div className="flex items-center gap-2 pb-3" style={{ borderBottom: '2px dashed rgba(0,104,183,0.2)' }}>
                <span className="material-symbols-outlined text-[#216d1c]" style={{ fontSize: '16px', fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                <span className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#414751' }}>
                  スキャン完了: {scanResults.reduce((sum, r) => sum + r.count, 0)}件の飛行計画を{scanResults.length}日で発見
                </span>
              </div>
              <div className="space-y-2">
                {scanResults.map((result, idx) => (
                  <div
                    key={idx}
                    className="p-3 rounded-xl border-2"
                    style={{ backgroundColor: 'rgba(164,78,1,0.04)', borderColor: 'rgba(164,78,1,0.3)' }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, color: '#7f3b00' }}>
                        {result.date}
                      </span>
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border-2"
                        style={{ backgroundColor: '#FFF7ED', borderColor: '#a44e01', color: '#7f3b00', fontFamily: 'Space Grotesk, sans-serif' }}
                      >
                        {result.count}件
                      </span>
                    </div>
                    <div className="space-y-1">
                      {result.plans.map((plan, pIdx) => (
                        <div
                          key={pIdx}
                          className="text-sm p-2 rounded-lg border cursor-pointer transition-all"
                          style={{
                            backgroundColor: '#ffffff',
                            borderColor: 'rgba(164,78,1,0.2)',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(164,78,1,0.06)'; e.currentTarget.style.borderColor = 'rgba(164,78,1,0.5)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; e.currentTarget.style.borderColor = 'rgba(164,78,1,0.2)'; }}
                          onClick={() => setSelectedFlightPlan(plan)}
                        >
                          <div className="flex items-center justify-between">
                            <span style={{ fontFamily: 'Inter, sans-serif', color: '#414751' }}>
                              {plan.flight_purpose || plan.purpose || plan.flightPurpose || '飛行計画'}
                            </span>
                            <span className="material-symbols-outlined" style={{ fontSize: '14px', color: '#a44e01' }}>chevron_right</span>
                          </div>
                          {(plan.flight_area || plan.flightArea) && (
                            <div className="text-xs mt-1 flex items-center gap-1" style={{ color: '#717782' }}>
                              <span className="material-symbols-outlined" style={{ fontSize: '12px' }}>location_on</span>
                              {plan.flight_area || plan.flightArea}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : flightPlans.length > 0 ? (
            // 単日検索結果
            <div className="space-y-2">
              {flightPlans.map((plan, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-xl border-2 cursor-pointer transition-all"
                  style={{ backgroundColor: 'rgba(164,78,1,0.04)', borderColor: 'rgba(164,78,1,0.3)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(164,78,1,0.08)'; e.currentTarget.style.borderColor = '#a44e01'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(164,78,1,0.04)'; e.currentTarget.style.borderColor = 'rgba(164,78,1,0.3)'; }}
                  onClick={() => setSelectedFlightPlan(plan)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, color: '#7f3b00' }}>
                        {plan.flight_purpose || plan.purpose || '飛行計画'}
                      </div>
                      <div className="text-sm mt-1" style={{ fontFamily: 'Inter, sans-serif', color: '#414751' }}>
                        {formatDate(plan.flight_start_datetime || plan.start_date)} ～ {formatDate(plan.flight_end_datetime || plan.end_date)}
                      </div>
                      {plan.flight_area && (
                        <div className="text-sm mt-1 flex items-center gap-1" style={{ color: '#717782' }}>
                          <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>location_on</span>
                          {plan.flight_area}
                        </div>
                      )}
                    </div>
                    <span
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border-2"
                      style={{ backgroundColor: '#FFF7ED', borderColor: '#a44e01', color: '#7f3b00', fontFamily: 'Space Grotesk, sans-serif' }}
                    >
                      {plan.status || '通報済み'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center py-12 text-center">
              <span className="material-symbols-outlined mb-4" style={{ fontSize: '48px', color: '#c1c7d3' }}>calendar_month</span>
              <p style={{ fontFamily: 'Inter, sans-serif', color: '#717782' }}>
                {searchDate} の飛行計画はありません
              </p>
              <p className="text-xs mt-2" style={{ fontFamily: 'Inter, sans-serif', color: '#717782' }}>
                「範囲スキャン」ボタンで過去2週間〜未来2ヶ月を検索できます
              </p>
            </div>
          )}
        </QuestWindow>
      )}

      {/* 飛行計画詳細モーダル */}
      <QuestModal
        isOpen={!!selectedFlightPlan}
        onClose={() => setSelectedFlightPlan(null)}
        title="飛行計画詳細"
        subtitle="Flight Plan Detail"
        size="lg"
      >
        {selectedFlightPlan && (
          <div>
            <SectionLabel>Raw Data</SectionLabel>
            <div
              className="mt-2 rounded-xl p-4 max-h-96 overflow-auto"
              style={{ border: '2px solid rgba(0,104,183,0.2)', backgroundColor: 'rgba(0,104,183,0.02)' }}
            >
              <pre
                className="text-xs whitespace-pre-wrap"
                style={{ fontFamily: 'monospace', color: '#414751' }}
              >
                {JSON.stringify(selectedFlightPlan, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </QuestModal>

      {/* 機体詳細ボトムシート */}
      <AircraftBottomSheet
        isOpen={isAircraftSheetOpen}
        onClose={closeAircraftSheet}
        aircraft={selectedAircraft}
      />
    </div>
  );
}

// 詳細行コンポーネント
function DetailRow({ label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="flex-shrink-0 w-28">
        <SectionLabel>{label}</SectionLabel>
      </span>
      <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#191c21' }}>
        {value || '—'}
      </span>
    </div>
  );
}
